import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RappiderMenuModule, RappiderMonacoEditorModule } from '@rappider/rappider-components';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { ComponentCompilingProcessStatus, ComponentGeneratorService } from '../../services/component-generator.service';
import { concatMap, forkJoin, map, mergeMap } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { set } from 'lodash';
import { Menu, MenuActionBehavior, MenuConfig, MenuMode } from '@rappider/rappider-components/utils';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzButtonModule } from 'ng-zorro-antd/button';


export const COMPONENT_INFO_MOCKED_FILE_NAME = 'component-manifest.json';

@Component({
  selector: 'rappider-component-definition-info-files',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzTabsModule,
    RappiderMonacoEditorModule,
    RappiderMenuModule,
    NzCollapseModule,
    NzSpinModule,
    NzButtonModule
  ],
  templateUrl: './component-definition-info-files.component.html',
  styleUrls: ['./component-definition-info-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RappiderComponentDefinitionInfoFilesComponent implements OnChanges {
  @Input() component: any;
  @Input() componentDefinition: any;
  @Input() visible = false;

  componentDefinitionFileContents: Record<string, string> = {
    COMPONENT_INFO_MOCKED_FILE_NAME: 'Loading...'
  };
  selectedFileName = COMPONENT_INFO_MOCKED_FILE_NAME;

  /* monaco editor options for component code */
  componentCodeEditorDefaultOptions = {
    language: 'typescript',
    lineNumbers: { renderType: 1 },
    minimap: {
      enabled: false,
    },
  };

  componentCodeEditorOptions = {};

  componentFilesMenu: MenuConfig = {
    items: [],
    mode: MenuMode.Vertical,
  };

  /* Used to display file saving status */
  isSaving = false;

  /**
   * Compilation status of the component.
   * This will be used to show the compilation status of the component.
   */
  compilationStatus: ComponentCompilingProcessStatus | null = null;

  private filesLoaded = false;

  constructor(
    private componentGeneratorService: ComponentGeneratorService,
    private cd: ChangeDetectorRef
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    // Only load files when component becomes visible and files haven't been loaded yet
    if (changes['visible'] && this.visible && !this.filesLoaded && this.componentDefinition) {
      this.getComponentDefinitionFiles();
    }
  }

  /**
   * Load component definition source files
   * Only loads files when component is visible (modal is open)
   */
  getComponentDefinitionFiles(): void {
    if (!this.componentDefinition?.files?.length) {
      return;
    }

    if (this.filesLoaded) {
      return;
    }

    forkJoin(
      this.componentDefinition.files.map(file =>
        this.componentGeneratorService.getComponentDefinitionFileContent(this.componentDefinition.id, file.name)
          .pipe(
            map(fileContent => ({
              filename: file.name,
              content: fileContent
            }))
          )
      )
    ).subscribe((fileContents: any[]) => {
      fileContents.push({
        filename: COMPONENT_INFO_MOCKED_FILE_NAME,
        content: JSON.stringify(this.componentDefinition || {}, null, 2)
      });

      fileContents.forEach((file) => {
        this.componentDefinitionFileContents[file.filename] = file.content;
      });

      this.componentFilesMenu.items = fileContents.map((file) => <Menu>{
        label: file.filename?.length >= 24 ? file?.filename?.toString().substring(0, 22) + '...' : file.filename,
        key: file.filename,
        icon: {
          name: this.getIconForFileType(file.filename)
        },
        tooltip: file.filename?.length >= 24 ? file.filename : null,
        actionBehavior: MenuActionBehavior.Emit,
      });

      this.selectedFileName = fileContents[0]?.filename || COMPONENT_INFO_MOCKED_FILE_NAME;
      this.setFileTypeForEditor(this.selectedFileName);
      this.filesLoaded = true;

      this.cd.detectChanges();
    });
  }

  // addFileToComponentDefinition(filename: string, fileContent: string) {
  //   if (!this.componentDefinition || !filename || !fileContent) {
  //     console.warn('No component definition, filename, or file content provided to add.');
  //     return;
  //   }
  //   this.componentDefinition.files = this.componentDefinition.files || [];
  //   if (!this.componentDefinition.files.some(f => f.name === fileName)) {
  //     this.componentDefinition.files.push({ name: fileName });

  getComponentCompilationStatus() {
    this.componentGeneratorService.getCompiledComponentStatus(this.componentDefinition?.id)
      .subscribe((status: ComponentCompilingProcessStatus) => {
        console.log('Component compilation status:', status);
        this.compilationStatus = status;
        this.cd.detectChanges();
      }, error => {
        console.error('Error fetching component compilation status:', error);
        this.compilationStatus = null;
      });
  }


  onTabChange(event: any) {
    console.log('Tab changed:', event);
  }

  getCodeLanguage(fileName: string): string {
    if (fileName.endsWith('.ts')) {
      return 'typescript';
    } else if (fileName.endsWith('.html')) {
      return 'html';
    } else if (fileName.endsWith('.css')) {
      return 'css';
    } else if (fileName.endsWith('.scss')) {
      return 'scss';
    } else if (fileName.endsWith('.json')) {
      return 'json';
    } else if (fileName.endsWith('.js')) {
      return 'javascript';
    } else {
      // get file extension
      const extension = fileName.split('.').pop();
      if (extension) {
        return extension;
      } else {
        return 'text';
      }
    }
  }

  getIconForFileType(fileName: string): string {
    const codeExtensions = ['.ts', '.js', '.jsx', '.tsx', '.py', '.java', '.c', '.cpp', '.go', '.php', '.rb'];
    const templateExtensions = ['.html', '.htm'];
    const styleExtensions = ['.css', '.scss'];
    const dataExtensions = ['.json', '.csv', '.xml'];
    if (fileName === COMPONENT_INFO_MOCKED_FILE_NAME) {
      return 'fa-regular fa-circle-info';
    } else if (codeExtensions.some(ext => fileName.endsWith(ext))) {
      // If the file is a code file, return the code icon
      return 'fa-regular fa-code';
    } else if (templateExtensions.some(ext => fileName.endsWith(ext))) {
      // If the file is a template file, return the template icon
      return 'fa-regular fa-objects-column';
    } else if (styleExtensions.some(ext => fileName.endsWith(ext))) {
      // If the file is a style file, return the style icon
      return 'fa-regular fa-palette';
    } else if (dataExtensions.some(ext => fileName.endsWith(ext))) {
      // If the file is a data file, return the data icon
      return 'fa-regular fa-table';
    } else {
      return 'fa-regular fa-file';
    }
  }

  fileNameOnMenuClicked(event: any) {
    console.log('File name clicked:', event);
    this.selectedFileName = event?.key || COMPONENT_INFO_MOCKED_FILE_NAME;
    this.setFileTypeForEditor(this.selectedFileName);
  }

  setFileTypeForEditor(fileName: string) {
    setTimeout(() => {
      this.componentCodeEditorOptions = {
        ...this.componentCodeEditorDefaultOptions,
        language: this.getCodeLanguage(fileName ?? 'json'),
        readonly: fileName === COMPONENT_INFO_MOCKED_FILE_NAME ? true : false
      };
      this.cd.detectChanges();
    }, 100);
  }

  saveComponentDefinitionFileContents() {
    if (!this.componentDefinition) {
      console.warn('No component definition available to save.');
      return;
    }

    this.isSaving = true;
    // trigger change detection for the component files menu
    this.cd.detectChanges();
    console.log(this.componentDefinitionFileContents);
    if (Object.keys(this.componentDefinitionFileContents).length) {
      const filesBody = Object.entries(this.componentDefinitionFileContents).map(([fileName, content]) => ({
        name: fileName,
        content: content,
        description: ''
      })).filter(file => file.name !== 'COMPONENT_INFO_MOCKED_FILE_NAME'); // Exclude the mocked file
      // You can use filesBody as needed here

      // update-files if filesBody
      if (filesBody?.length) {
        this.componentGeneratorService.updateComponentDefinitionFiles(this.componentDefinition.id, filesBody)
          .subscribe(() => {
            console.log('Component definition files updated successfully');
            this.isSaving = false;
            // trigger change detection for the component files menu
            this.cd.detectChanges();
          }, error => {
            console.error('Error updating component definition files:', error);
            this.isSaving = false;
            // trigger change detection for the component files menu
            this.cd.detectChanges();
          });
      }

    }

  }

}
